import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  buildEvidenceUrl,
  buildEvidenceLabel,
  isLinkable,
} from '../domain/sourceRef';

/* Kinds whose snippet is meaningful evidence on its own. Mirrors the
   backend's SUBSTANTIVE_SOURCE_KINDS — keep in sync if either side
   adds a new kind. */
const SUBSTANTIVE = new Set([
  'quote', 'search', 'fact', 'contradiction', 'read_doc', 'match',
]);

function kindRank(kind) {
  if (SUBSTANTIVE.has(kind)) return 0;
  if (kind === 'entity') return 1;
  return 2;  // list / none / unknown
}

function dedupeByDocId(citations) {
  const seen = new Map();
  for (const c of citations || []) {
    const id = c.document_id;
    if (!id) continue;
    const existing = seen.get(id);
    if (!existing) {
      seen.set(id, c);
      continue;
    }
    // Prefer the higher-signal entry for this doc: stronger kind first,
    // then verified, then a populated anchor (page/timestamp).
    const better = (
      kindRank(c.kind) < kindRank(existing.kind)
      || (kindRank(c.kind) === kindRank(existing.kind) && c.verified && !existing.verified)
      || (c.page != null && existing.page == null)
      || (c.timestamp != null && existing.timestamp == null)
    );
    if (better) seen.set(id, c);
  }
  return [...seen.values()];
}

function sortForPanel(citations) {
  return [...citations].sort((a, b) => {
    const ra = kindRank(a.kind);
    const rb = kindRank(b.kind);
    if (ra !== rb) return ra - rb;
    if (!!b.verified !== !!a.verified) return b.verified ? 1 : -1;
    return (a.filename || '').localeCompare(b.filename || '');
  });
}

const VISIBLE_CAP = 10;

/**
 * Sources panel — surfaces citations the agent verified this turn even
 * when the prose contains no inline [N] markers to anchor them. Pairs
 * with ChatMessage's existing inline-link renderer: inline links carry
 * per-claim attribution; this panel is "documents consulted." The two
 * are intentionally additive — same docs may appear in both when the
 * model cites and uses summary tools in the same turn.
 */
export default function SourcesPanel({ citations, pathname }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const items = useMemo(() => {
    if (!citations || citations.length === 0) return [];
    return sortForPanel(dedupeByDocId(citations)).filter(isLinkable);
  }, [citations]);

  if (items.length === 0) return null;

  const visible = expanded ? items : items.slice(0, VISIBLE_CAP);
  const hidden = items.length - visible.length;

  return (
    <div className="chat-sources-panel">
      <div className="chat-sources-panel-header">Sources</div>
      <ul className="chat-sources-panel-list">
        {visible.map((c) => {
          const href = buildEvidenceUrl(c, pathname);
          if (!href) return null;
          const glyph = c.verified === false ? '⚠' : '📄';
          const label = buildEvidenceLabel(c);
          // Same-origin links navigate via React Router to keep the
          // viewer modal in-tab — a full reload re-mounts AuthProvider
          // and races the viewer's fetches against token init (401).
          const isInApp = href.startsWith('/') || href.startsWith('?') || href.startsWith('#');
          const onClick = isInApp
            ? (e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                navigate(href);
              }
            : undefined;
          const linkProps = isInApp
            ? { href, className: 'citation-link', onClick }
            : { href, target: '_blank', rel: 'noopener noreferrer', className: 'citation-link' };
          return (
            <li key={c.document_id}>
              <a {...linkProps}>{`${glyph} ${label}`}</a>
            </li>
          );
        })}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          className="chat-sources-panel-more"
          onClick={() => setExpanded(true)}
        >
          Show all ({items.length})
        </button>
      )}
    </div>
  );
}
